import type { AutomationId } from "@repo/app-types/entities/automation";
import type {
  AutomationArtifactDetail,
  AutomationArtifactId,
  AutomationArtifactSummary,
} from "@repo/app-types/entities/automationArtifact";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

/** Where a saved report lives, resolved from the result id alone (redirect). */
export interface AutomationArtifactLocation {
  automation_id: AutomationId;
  playbook_id: PlaybookId | null;
}

const automationArtifactApi = api.injectEndpoints({
  endpoints: (builder) => ({
    automationArtifacts: builder.query<
      AutomationArtifactSummary[],
      AutomationId,
      { data: { automation_artifacts: AutomationArtifactSummary[] } }
    >({
      query: (automationId) =>
        `/automations/${encodeURIComponent(automationId)}/artifacts`,
      transformResponse: (response) => response.data.automation_artifacts,
      providesTags: (result, _error, automationId) =>
        getTagsProvidedByList(TagType.AutomationArtifact, result, {
          filteredBy: [automationId],
        }),
    }),
    automationArtifactById: builder.query<
      AutomationArtifactDetail,
      {
        automationId: AutomationId;
        automationArtifactId: AutomationArtifactId;
      },
      { data: { automation_artifact: AutomationArtifactDetail } }
    >({
      query: ({ automationId, automationArtifactId }) =>
        `/automations/${encodeURIComponent(automationId)}/artifacts/${encodeURIComponent(automationArtifactId)}`,
      transformResponse: (response) => response.data.automation_artifact,
      providesTags: (_result, _error, { automationArtifactId }) =>
        getTagsProvidedByEntity(
          TagType.AutomationArtifact,
          automationArtifactId,
        ),
    }),
    automationArtifactLocation: builder.query<
      AutomationArtifactLocation,
      AutomationArtifactId,
      { data: AutomationArtifactLocation }
    >({
      query: (automationArtifactId) =>
        `/automations/artifacts/${encodeURIComponent(automationArtifactId)}`,
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, automationArtifactId) =>
        getTagsProvidedByEntity(
          TagType.AutomationArtifact,
          automationArtifactId,
        ),
    }),
    // Teammate-facing "Notifications": the automation results delivered to the
    // current teammate. Backed by the recipient-scoped `/me/artifacts`
    // endpoints, which authorize by recipient membership rather than the
    // automations permission a Team Member lacks.
    myAutomationResults: builder.infiniteQuery<
      {
        artifacts: AutomationArtifactSummary[];
        nextCursor: string | null;
      },
      { playbookId: PlaybookId },
      string | null,
      {
        data: { automation_artifacts: AutomationArtifactSummary[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/automations/me/artifacts`,
        params: {
          cursor: pageParam ?? undefined,
          playbook_id: queryArg.playbookId,
        },
      }),
      transformResponse: (response) => ({
        artifacts: response.data.automation_artifacts,
        nextCursor: response.next_cursor,
      }),
      providesTags: (result) =>
        getTagsProvidedByList(
          TagType.AutomationArtifact,
          result?.pages.flatMap((page) => page.artifacts),
        ),
    }),
    myAutomationResultById: builder.query<
      AutomationArtifactDetail,
      {
        automationArtifactId: AutomationArtifactId;
        playbookId: PlaybookId;
      },
      { data: { automation_artifact: AutomationArtifactDetail } }
    >({
      query: ({ automationArtifactId, playbookId }) => ({
        url: `/automations/me/artifacts/${encodeURIComponent(automationArtifactId)}`,
        params: { playbook_id: playbookId },
      }),
      transformResponse: (response) => response.data.automation_artifact,
      providesTags: (_result, _error, { automationArtifactId }) =>
        getTagsProvidedByEntity(
          TagType.AutomationArtifact,
          automationArtifactId,
        ),
    }),
    myAutomationResultLocation: builder.query<
      AutomationArtifactLocation,
      AutomationArtifactId,
      { data: AutomationArtifactLocation }
    >({
      query: (automationArtifactId) =>
        `/automations/me/artifacts/${encodeURIComponent(automationArtifactId)}/location`,
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, automationArtifactId) =>
        getTagsProvidedByEntity(
          TagType.AutomationArtifact,
          automationArtifactId,
        ),
    }),
  }),
});

export const {
  useAutomationArtifactsQuery,
  useAutomationArtifactByIdQuery,
  useAutomationArtifactLocationQuery,
  useMyAutomationResultsInfiniteQuery,
  useMyAutomationResultByIdQuery,
  useMyAutomationResultLocationQuery,
} = automationArtifactApi;
